import { type FC, useCallback, useEffect } from 'react';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { Result } from '@/lib/http/HttpClient';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import { X } from 'lucide-react';
import { IconButton } from '@/components/ui/icon-button';
import type { ToolResponse } from '../api/TraceabilityResponseType';
import type { Trace } from '../type/Tool';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { toolState as toolStateOptions } from '../data/toolState';
import { formatCompanyName } from '@/utils/format';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card/Card';
import { ToolStateBadge } from '@/components/shared/ToolStateBadge';

interface ToolIdentityStripProps {
  toolId: string;
  traceabilityApiClient: TraceabilityApiClient;
  onClose?: () => void;
  setCompanyName: (name: string) => void;
  onValid: (valid: boolean) => void;
  setToolReference?: (ref: string | null) => void;
}

export const ToolIdentityStrip: FC<ToolIdentityStripProps> = ({
  toolId,
  traceabilityApiClient,
  onClose,
  setCompanyName,
  onValid,
  setToolReference,
}) => {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  const fetchToolData = useCallback(
    async (signal: AbortSignal) => {
      return new Promise<Result<ToolResponse>>((resolve, reject) => {
        let settled = false;

        const handler = new JobRequestHandler<ToolResponse>({
          initFn: () => traceabilityApiClient.toolRead({ toolId }, { signal }),
          statusFn: (jobId) => traceabilityApiClient.toolStatus({ jobId }, { signal }),
          signal,
          setResult: (res) => {
            if (settled || !res) return;
            settled = true;

            if (res.ok) {
              resolve({
                ok: true,
                data: res.data,
                timestamp: new Date().toISOString(),
              } as Result<ToolResponse>);
            } else {
              resolve({
                ok: false,
                code: res.code,
                name: res.name,
                message: res.message,
                timestamp: res.timestamp || new Date().toISOString(),
              } as Result<ToolResponse>);
            }
          },
        });

        handler.start().catch((err) => {
          if (settled) return;
          settled = true;
          if ((err instanceof Error || err instanceof DOMException) && err.name === 'AbortError') {
            reject(err);
            return;
          }
          console.error('Failed to load tool data', err);
          resolve({
            ok: false,
            code: 500,
            name: err instanceof Error ? err.name : 'UnknownError',
            message: err instanceof Error ? err.message : String(err),
            timestamp: new Date().toISOString(),
          });
        });
      });
    },
    [toolId, traceabilityApiClient]
  );

  const handleSuccess = useCallback(
    (data: ToolResponse) => {
      setCompanyName(data.companyName || '');
      onValid(true);
      setToolReference?.(data.reference ?? null);
    },
    [setCompanyName, onValid, setToolReference]
  );

  const handleError = useCallback(() => {
    onValid(false);
  }, [onValid]);

  const {
    data: result,
    error,
    isLoading,
  } = useApiCall<ToolResponse>({
    apiFn: fetchToolData,
    deps: [toolId],
    onSuccess: handleSuccess,
    onError: handleError,
  });

  useEffect(() => {
    if (!onClose) return;
    const timer = setTimeout(onClose, 60_000);
    return () => clearTimeout(timer);
  }, [onClose]);

  if (isLoading) {
    return <Skeleton className="h-10 w-full rounded-xl" />;
  }

  if (error || !result) {
    return null;
  }

  const tool = result;
  const totalTraces = tool.traces?.length ?? 0;
  const latestTrace: Trace | undefined = tool.traces?.[0];
  const latestState = latestTrace?.toolState?.toolState;
  // Missing or unrecognised states read as "unknown"
  const latestStatus = toolStateOptions.some((option) => option.value === latestState)
    ? latestState
    : 'unknown';

  return (
    <Card
      as="dl"
      tier="muted"
      padding="none"
      className="flex flex-wrap items-center gap-0 px-4 py-2 text-sm"
    >
      <div className="flex flex-col px-3 py-1 border-r border-border/60 last:border-r-0 gap-0.5">
        <dt className="text-xs text-muted-foreground">{t('traceability.toolInfo.badge')}</dt>
        <dd className="font-mono font-bold text-foreground">{toolId}</dd>
      </div>
      <div className="flex flex-col px-3 py-1 border-r border-border/60 last:border-r-0 gap-0.5">
        <dt className="text-xs text-muted-foreground">
          {t('traceability.toolInfo.fields.reference')}
        </dt>
        {(() => {
          const resolved = resolve(tool.reference ?? '');
          return (
            <>
              <dd className="text-foreground">
                {resolved.label || t('traceability.toolInfo.fallback.notProvided')}
              </dd>
              {resolved.hasAlias && (
                <dd className="text-xs text-muted-foreground font-mono">{resolved.canonical}</dd>
              )}
            </>
          );
        })()}
      </div>
      <div className="flex flex-col px-3 py-1 border-r border-border/60 last:border-r-0 gap-0.5">
        <dt className="text-xs text-muted-foreground">
          {t('traceability.toolInfo.fields.company')}
        </dt>
        <dd className="text-foreground">
          {tool.companyName
            ? formatCompanyName(tool.companyName)
            : t('traceability.toolInfo.fallback.notProvided')}
        </dd>
      </div>
      <div className="flex flex-col px-3 py-1 border-r border-border/60 last:border-r-0 gap-0.5">
        <dt className="text-xs text-muted-foreground">
          {t('traceability.toolInfo.fields.latestStatus')}
        </dt>
        <dd>
          <ToolStateBadge toolState={latestStatus} />
        </dd>
      </div>
      <div className="flex flex-col px-3 py-1 border-r border-border/60 last:border-r-0 gap-0.5">
        <dt className="text-xs text-muted-foreground">
          {t('traceability.toolInfo.fields.totalTraces')}
        </dt>
        <dd className="text-foreground">{totalTraces}</dd>
      </div>
      {onClose && (
        <div className="ml-auto pl-3">
          <IconButton icon={X} label={t('traceability.toolInfo.actions.close')} onClick={onClose} />
        </div>
      )}
    </Card>
  );
};
